import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LayoutGrid, FileText, Wrench, Bot, CreditCard, TrendingUp, Receipt, LogOut } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { LanguageSwitcher } from "@/components/language-switcher";
import { useAuth } from "@/providers/AuthProvider";

type NavLabelKey =
  | "instances"
  | "llmTemplates"
  | "llmTools"
  | "aiModels"
  | "billing"
  | "margin"
  | "payments";

type NavItem = {
  to: string;
  labelKey: NavLabelKey;
  Icon: typeof LayoutGrid;
  end?: boolean;
};

const NAV: { sectionKey: "provisioning" | "configuration"; items: NavItem[] }[] = [
  {
    sectionKey: "provisioning",
    items: [{ to: "/", labelKey: "instances", Icon: LayoutGrid, end: true }],
  },
  {
    sectionKey: "configuration",
    items: [
      { to: "/templates", labelKey: "llmTemplates", Icon: FileText },
      { to: "/tools", labelKey: "llmTools", Icon: Wrench },
      { to: "/templates/ai-assistant/settings", labelKey: "aiModels", Icon: Bot },
      { to: "/billing", labelKey: "billing", Icon: CreditCard, end: true },
      { to: "/billing/margin", labelKey: "margin", Icon: TrendingUp },
      { to: "/billing/payments", labelKey: "payments", Icon: Receipt },
    ],
  },
];

export function Sidebar() {
  const { t } = useTranslation("layout");
  const { user, signOut } = useAuth();

  return (
    <aside className="flex h-screen w-60 shrink-0 flex-col border-r bg-card">
      <div className="flex items-center gap-2 px-4 py-5">
        <div
          className="h-6 w-6 rounded-md"
          style={{ background: "linear-gradient(135deg, #6366f1, #06b6d4)" }}
        />
        <span className="font-semibold tracking-tight">{t("brand")}</span>
      </div>

      <Separator />

      <nav className="flex-1 overflow-y-auto px-2 py-3">
        {NAV.map((section) => (
          <div key={section.sectionKey} className="mb-3">
            <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              {t(`nav.${section.sectionKey}`)}
            </div>
            {section.items.map(({ to, labelKey, Icon, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium transition-colors",
                    isActive
                      ? "bg-accent text-accent-foreground"
                      : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                  )
                }
              >
                <Icon className="h-4 w-4" />
                <span>{t(`nav.${labelKey}`)}</span>
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      <Separator />

      <div className="space-y-2 px-3 py-3">
        <div className="flex items-center justify-between gap-2">
          <div className="truncate text-xs text-muted-foreground" title={user?.email ?? ""}>
            {user?.email ?? "—"}
          </div>
          <LanguageSwitcher className="shrink-0" />
        </div>
        <Button
          variant="outline"
          size="sm"
          className="w-full justify-start"
          onClick={() => void signOut()}
        >
          <LogOut className="h-4 w-4" />
          <span>{t("signOut")}</span>
        </Button>
      </div>
    </aside>
  );
}
